import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, sinceMs, type LogEvent, type LogGroup, type LogStream } from "../api";
import { EventList } from "../components/EventList";
import { useGroups, useLogGroups } from "../hooks";
import { useSession } from "../session";

const RETENTION = [1, 3, 7, 14, 30, 90, 180, 365];

function AdminForms() {
  const qc = useQueryClient();
  const groups = useGroups();
  const [groupName, setGroupName] = useState("");
  const [lg, setLg] = useState({ group: "", name: "", retention_days: 30 });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["groups"] });
    qc.invalidateQueries({ queryKey: ["log-groups"] });
  };
  const createGroup = useMutation({
    mutationFn: () => api("/v1/groups", { method: "POST", body: { name: groupName } }),
    onSuccess: () => { setGroupName(""); setError(null); refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const createLg = useMutation({
    mutationFn: () => api("/v1/log-groups", { method: "POST", body: lg }),
    onSuccess: () => { setLg({ ...lg, name: "" }); setError(null); refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <details className="admin-forms">
      <summary>Create group / log group</summary>
      <form onSubmit={(e) => { e.preventDefault(); createGroup.mutate(); }} className="row">
        <label>
          Group name
          <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="billing/prod" required />
        </label>
        <button type="submit">Create group</button>
      </form>
      <form onSubmit={(e) => { e.preventDefault(); createLg.mutate(); }} className="row">
        <label>
          Group
          <select value={lg.group} onChange={(e) => setLg({ ...lg, group: e.target.value })} required>
            <option value="">—</option>
            {groups.data?.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </label>
        <label>
          Log group
          <input value={lg.name} onChange={(e) => setLg({ ...lg, name: e.target.value })} placeholder="api" required />
        </label>
        <label>
          Retention (days)
          <select value={lg.retention_days} onChange={(e) => setLg({ ...lg, retention_days: Number(e.target.value) })}>
            {RETENTION.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <button type="submit">Create log group</button>
      </form>
      {error && <p role="alert" className="error">{error}</p>}
    </details>
  );
}

function Query({ lg }: { lg: LogGroup }) {
  const [form, setForm] = useState({ pattern: "", range: "1h", prefix: "" });
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const streams = useQuery({
    queryKey: ["streams", lg.id],
    queryFn: () => api<{ log_streams: LogStream[] }>(`/v1/log-groups/${lg.id}/streams`, { query: { order_by: "last_event", limit: 50 } }),
  });

  async function run(next: string | null) {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ events: LogEvent[]; next_token: string | null }>("/v1/logs/filter", {
        method: "POST",
        body: {
          group: lg.group_name, log_group: lg.name, pattern: form.pattern,
          start_time: sinceMs(form.range), log_stream_name_prefix: form.prefix || undefined,
          limit: 500, next_token: next ?? undefined,
        },
      });
      setEvents((prev) => (next ? [...prev, ...res.events] : res.events));
      setToken(res.next_token);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="query-title">
      <h2 id="query-title">{lg.group_name} / {lg.name} <span className="muted">· {lg.retention_days}d retention</span></h2>
      <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); run(null); }}>
        <label className="grow">
          Filter pattern
          <input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                 placeholder={'ERROR  or  { $.level = "ERROR" && $.ms > 500 }'} />
        </label>
        <label>
          Range
          <select value={form.range} onChange={(e) => setForm({ ...form, range: e.target.value })}>
            {["15m", "1h", "6h", "24h", "7d", "30d", "all"].map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <label>
          Stream prefix
          <input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} list="streams" />
          <datalist id="streams">
            {streams.data?.log_streams.map((s) => <option key={s.id} value={s.name} />)}
          </datalist>
        </label>
        <button type="submit" disabled={busy}>{busy ? "Searching…" : "Search"}</button>
      </form>
      {error && <p role="alert" className="error">{error}</p>}
      <EventList events={events} label="Matching log events" />
      {token && <button type="button" onClick={() => run(token)} disabled={busy}>Load more</button>}
    </section>
  );
}

export function ExplorerPage() {
  const { can } = useSession();
  const groups = useGroups();
  const logGroups = useLogGroups();
  const [selected, setSelected] = useState<string | null>(null);
  const lg = logGroups.data?.find((l) => l.id === selected) ?? null;

  return (
    <div className="split">
      <aside aria-label="Groups and log groups" className="tree">
        {groups.isLoading && <p role="status">Loading…</p>}
        {groups.data?.map((g) => {
          const children = logGroups.data?.filter((l) => l.group_id === g.id) ?? [];
          return (
            <div key={g.id}>
              <h3>{g.name}</h3>
              <ul>
                {children.map((l) => (
                  <li key={l.id}>
                    <button type="button" className={`link ${selected === l.id ? "active" : ""}`}
                            aria-current={selected === l.id ? "true" : undefined} onClick={() => setSelected(l.id)}>
                      {l.name}
                    </button>
                  </li>
                ))}
                {!children.length && <li className="muted">no log groups</li>}
              </ul>
            </div>
          );
        })}
        {can("admin") && <AdminForms />}
      </aside>
      <div className="content">
        {lg ? <Query key={lg.id} lg={lg} /> : <p className="muted">Select a log group to search its events.</p>}
      </div>
    </div>
  );
}
